import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface AppState {
  selectedVersion: string;
  selectedBook: string;
  selectedChapter: number;
  selectedVerse: string | null;
  isLoading: boolean;
  setSelectedVersion: (version: string) => void;
  setSelectedBook: (book: string) => void;
  setSelectedChapter: (chapter: number) => void;
  setSelectedVerse: (verse: string | null) => void;
  loadBookmark: () => Promise<void>;
  updateBookmark: (book: string, chapter: number) => Promise<void>;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      selectedVersion: 'kjv',
      selectedBook: 'Genesis',
      selectedChapter: 1,
      selectedVerse: null,
      isLoading: false,

      setSelectedVersion: (version) => set({ selectedVersion: version }),

      setSelectedBook: (book) => {
        set({ selectedBook: book });
        get().updateBookmark(book, get().selectedChapter);
      },

      setSelectedChapter: (chapter) => {
        set({ selectedChapter: chapter });
        get().updateBookmark(get().selectedBook, chapter);
      },

      setSelectedVerse: (verse) => set({ selectedVerse: verse }),

      loadBookmark: async () => {
        set({ isLoading: true });
        try {
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            throw new Error('No authenticated user');
          }

          const { data: bookmarkData, error } = await supabase
            .from('bible_bookmarks')
            .select('book, chapter')
            .eq('user_id', user.id)
            .single();

          if (error && error.code !== 'PGRST116') throw error;

          if (bookmarkData) {
            // Update state with bookmark data
            set({ 
              selectedBook: bookmarkData.book,
              selectedChapter: bookmarkData.chapter,
              isLoading: false 
            });

            // Save to localStorage as a backup
            localStorage.setItem('last-bible-position', JSON.stringify({
              book: bookmarkData.book,
              chapter: bookmarkData.chapter
            }));
          }
        } catch (error) {
          console.error('Error loading bookmark:', error);
          // Try to load from localStorage as fallback
          const savedPosition = localStorage.getItem('last-bible-position');
          if (savedPosition) {
            const { book, chapter } = JSON.parse(savedPosition);
            set({ selectedBook: book, selectedChapter: chapter });
          }
        } finally {
          set({ isLoading: false });
        }
      },

      updateBookmark: async (book: string, chapter: number) => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            // Save to localStorage if not authenticated
            localStorage.setItem('last-bible-position', JSON.stringify({ book, chapter }));
            return;
          }

          const { error } = await supabase
            .from('bible_bookmarks')
            .upsert(
              { 
                user_id: user.id,
                book,
                chapter,
                updated_at: new Date().toISOString()
              },
              { 
                onConflict: 'user_id',
                ignoreDuplicates: false
              }
            );

          if (error) throw error;

          // Save to localStorage as backup
          localStorage.setItem('last-bible-position', JSON.stringify({ book, chapter }));
        } catch (error) {
          console.error('Error updating bookmark:', error);
          // Save to localStorage as fallback
          localStorage.setItem('last-bible-position', JSON.stringify({ book, chapter }));
        }
      },
    }),
    {
      name: 'divine-intelligence-bible-state',
    }
  )
);
